import { styled } from '@linaria/react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { FormControlLabel, IconButton, Menu, MenuItem, Switch } from '@mui/material';
import type { FC, KeyboardEvent, MouseEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayerView, perimeterQuery } from '../../../../core/mapV2/queries';
import { applyTableFiltersSet, layerExpandedToggled, layerVisibilitySet } from '../../../../core/mapV2/state';
import theme from '../../../../design/theme';
import { useAppDispatch, useAppSelector } from '../../../../types';
import { IndicatorSelect } from './IndicatorSelect';
import { LAYER_ICONS, LAYER_MENU_ITEMS } from './layerIcons';

const Card = styled.section`
    border: 1px solid ${theme.color.grey.medium};
    border-left: 3px solid transparent;
    border-radius: 8px;
    background-color: ${theme.color.white.main};
    overflow: hidden;

    &[data-visible='true'] {
        border-color: ${theme.color.blue.light3};
        border-left-color: ${theme.color.blue.primary};
        background-color: ${theme.color.blue.tint2};
    }

    &[data-available='false'] {
        opacity: 0.55;
    }
`;

const Head = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px 6px 6px;
    cursor: pointer;
    outline-offset: -2px;

    &[data-available='false'] {
        cursor: default;
    }
`;

const Text = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
`;

const Name = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${theme.color.grey.dark};

    &[data-visible='true'] {
        color: ${theme.color.blue.primary};
    }
`;

const Sub = styled.span`
    font-size: 11.5px;
    color: ${theme.color.grey.light};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 4px 12px 12px 12px;
`;

const FieldLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${theme.color.grey.main};
`;

type Props = { layer: LayerView };

export const LayerCard: FC<Props> = ({ layer }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const Icon = LAYER_ICONS[layer.key];
    const menuItems = LAYER_MENU_ITEMS[layer.key];
    const perimeter = useAppSelector(perimeterQuery);
    // The référentiel card also tells how many sections are in the perimeter (filters applied).
    const countKey = perimeter.restricted ? 'mapV2.perimeter.filtered' : 'mapV2.perimeter.count';
    const countText =
        layer.key === 'reference' && perimeter.total > 0
            ? t(countKey, { matching: perimeter.matching.length, total: perimeter.total, count: perimeter.total })
            : null;
    const subText = layer.available
        ? [layer.indicator, countText].filter(Boolean).join(' · ')
        : t('mapV2.layers.comingSoon');

    const toggleExpanded = () => dispatch(layerExpandedToggled(layer.key));
    const onHeadKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleExpanded();
        }
    };
    const stop = (event: MouseEvent | KeyboardEvent) => event.stopPropagation();

    return (
        <Card data-testid={`map-v2-layer-${layer.key}`} data-visible={layer.visible} data-available={layer.available}>
            <Head
                role="button"
                tabIndex={layer.available ? 0 : -1}
                aria-expanded={layer.open}
                aria-disabled={!layer.available}
                data-available={layer.available}
                title={layer.available ? t(layer.open ? 'mapV2.layers.collapse' : 'mapV2.layers.expand') : undefined}
                onClick={layer.available ? toggleExpanded : undefined}
                onKeyDown={layer.available ? onHeadKeyDown : undefined}
            >
                {layer.open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                <Icon fontSize="small" color={layer.visible ? 'primary' : 'disabled'} />
                <Text>
                    <Name data-visible={layer.visible}>{layer.label}</Name>
                    <Sub>{subText}</Sub>
                </Text>
                {layer.available && menuItems.length > 0 ? (
                    <IconButton
                        size="small"
                        aria-label={t('mapV2.layers.options')}
                        title={t('mapV2.layers.options')}
                        onClick={(event) => {
                            stop(event);
                            setMenuAnchor(event.currentTarget);
                        }}
                    >
                        <MoreHorizIcon fontSize="small" />
                    </IconButton>
                ) : null}
                <span
                    onClick={stop}
                    onKeyDown={stop}
                    role="presentation"
                    data-testid={`map-v2-layer-switch-${layer.key}`}
                >
                    <Switch
                        size="small"
                        checked={layer.visible}
                        disabled={!layer.available}
                        inputProps={{ 'aria-label': t(layer.visible ? 'mapV2.layers.hide' : 'mapV2.layers.show') }}
                        onChange={(_, checked) => dispatch(layerVisibilitySet({ layer: layer.key, visible: checked }))}
                    />
                </span>
            </Head>

            <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
                {menuItems.map((key) => (
                    <MenuItem key={key} disabled sx={{ fontSize: 13 }}>
                        {t(key)}
                        <span style={{ marginLeft: 12, fontSize: 11 }}>{t('mapV2.layers.comingSoon')}</span>
                    </MenuItem>
                ))}
            </Menu>

            {layer.open && layer.available ? (
                <Body>
                    <FieldLabel>{t('mapV2.layers.indicator')}</FieldLabel>
                    <IndicatorSelect
                        layer={layer.key}
                        value={layer.indicator}
                        ariaLabel={`${t('mapV2.layers.indicator')} ${layer.label}`}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={layer.applyTableFilters}
                                onChange={(_, value) => dispatch(applyTableFiltersSet({ layer: layer.key, value }))}
                            />
                        }
                        label={t('mapV2.layers.applyTableFilters')}
                        slotProps={{ typography: { fontSize: 12.5 } }}
                    />
                </Body>
            ) : null}
        </Card>
    );
};
